import React from 'react';
import axios from "axios";
import { useEffect, useState } from "react";
import config from "../../config";
import { BsFiletypeCsv } from "react-icons/bs";
import { useSelector } from "react-redux";
import { set } from 'date-fns';
import { Link } from 'react-router-dom';
import { TbTableExport } from "react-icons/tb";
import { format } from 'date-fns';

const AllocationHistoryReport = () => {

    const companyId = useSelector((state) => state.user.companyId);
    const [baskets, setBaskets] = useState([])
    const [list, setList] = useState([])
    const [allData, setAllData] = useState([])
    const [uniqueDates, setUniqueDates] = useState([])
    const [uniqueScripNames, setUniqueScripNames] = useState([])
    const [selectedDate, setSelectedDate] = useState("all")
    const [selectedScrip, setSelectedScrip] = useState("all")
    const [isLoadingBaskets, setIsLoadingBaskets] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(false)
    const [err, setErr] = useState("")

    useEffect(() => {
        const getBaskets = async (e) => {
            setIsLoadingBaskets(true);
            try {
                const res = await axios.get(config.BL_API_URL + "/basket/v1/getBasketByCompanyId", { params: { companyId } });

                if (res.data) {
                    setBaskets(res.data);
                } else {
                    setErr("backend error");
                }

            } catch (err) {
                //console.log("first")
                if (err.response) {
                    setErr(err.response.data);
                } else {
                    setErr("Server error...");
                }
            } finally {
                setIsLoadingBaskets(false);
            }
        }
        getBaskets();
    }, [])

    const onSelectionChange = async (e) => {
        if (e.target.value === "-") {
            // Reset all states when no basket is selected
            setAllData([]);
            setList([]);
            setUniqueDates([]);
            setUniqueScripNames([]);
            setSelectedDate("all");
            setSelectedScrip("all");
            return;
        }

        setIsLoadingData(true);
        try {
            let basketId = Number(e.target.value);
            const res = await axios.get(config.BL_API_URL + "/basketstock/v1/getBasketAllocationHistory", { params: { basketId } });

            if (res.data) {
                setAllData(res.data);
                
                // Extract unique dates and sort them
                const dates = [...new Set(res.data.map(item => format(item.publishedDate, 'dd/MMM/yyyy')))];
                const sortedDates = dates.sort((a, b) => new Date(b) - new Date(a)); // Sort descending
                setUniqueDates(sortedDates);
                
                // Extract unique scrip names and sort them alphabetically
                const scripNames = [...new Set(res.data.map(item => item.scripName))];
                const sortedScripNames = scripNames.sort();
                setUniqueScripNames(sortedScripNames);
                
                // Set highest date as default and reset scrip selection
                const highestDate = sortedDates[0];
                setSelectedDate(highestDate || "all");
                setSelectedScrip("all");
                
                // Filter data for the highest date or show all if no dates
                if (highestDate) {
                    const filteredData = res.data.filter(item => 
                        format(item.publishedDate, 'dd/MMM/yyyy') === highestDate
                    );
                    setList(filteredData);
                } else {
                    setList(res.data);
                }
            } else {
                setErr("backend error");
            }

        } catch (err) {
            //console.log("first")
            if (err.response) {
                setErr(err.response.data);
            } else {
                setErr("Server error...");
            }
        } finally {
            setIsLoadingData(false);
        }
        //setSelectedBatchId(e.target.value);
    }

    const onDateSelectionChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedDate(selectedValue);
        
        // Reset scrip selection when date changes
        setSelectedScrip("all");
        
        if (selectedValue === "all") {
            setList(allData);
        } else {
            const filteredData = allData.filter(item => 
                format(item.publishedDate, 'dd/MMM/yyyy') === selectedValue
            );
            setList(filteredData);
        }
    }

    const onScripSelectionChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedScrip(selectedValue);
        
        let dataToFilter = allData;
        
        // First apply date filter if not "all"
        if (selectedDate !== "all") {
            dataToFilter = allData.filter(item => 
                format(item.publishedDate, 'dd/MMM/yyyy') === selectedDate
            );
        }
        
        // Then apply scrip filter if not "all"
        if (selectedValue === "all") {
            setList(dataToFilter);
        } else {
            const filteredData = dataToFilter.filter(item => 
                item.scripName === selectedValue
            );
            setList(filteredData);
        }
    }

    const getDeltaIndicator = (deltaValue, currentAllocation) => {
        const delta = parseFloat(deltaValue);
        const allocation = parseFloat(currentAllocation);
        
        // If current allocation is 0, stock has been exited
        if (allocation === 0) {
            return {
                icon: "🚪",
                color: "text-orange-600",
                bgColor: "bg-orange-50",
                text: "Exited"
            };
        }
        
        if (delta > 0) {
            return {
                icon: "↗",
                color: "text-green-600",
                bgColor: "bg-green-50",
                text: "Increased"
            };
        } else if (delta < 0) {
            return {
                icon: "↘",
                color: "text-red-600",
                bgColor: "bg-red-50",
                text: "Decreased"
            };
        } else if (delta === 0) {
            return {
                icon: "→",
                color: "text-blue-600",
                bgColor: "bg-blue-50",
                text: "Unchanged"
            };
        } else {
            return {
                icon: "✕",
                color: "text-gray-600",
                bgColor: "bg-gray-50",
                text: "Removed"
            };
        }
    }

    function formatDate(value) {
        if (value == null || value === undefined || value === "") {
            return;
        }
        return new Intl.DateTimeFormat('en-IN').format(value);
    }

    return (
        <div>
            <div className="my-14 md:px-14 p-2 max-w-screen-xl mx-auto">
                <div className="flex flex-col">
                    <div className="-m-1.5 overflow-x-auto">
                        <div className="border rounded-lg divide-y divide-gray-200 min-w-full py-5">
                            <div className='col-span-2'>
                                <div className="col-span-1">
                                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 py-4 px-6'>
                                        <div className='flex flex-col lg:flex-row lg:items-center gap-2'>
                                            <label className='text-left font-medium whitespace-nowrap min-w-fit'>Basket Name</label>
                                            <div className="flex items-center gap-2 w-full">
                                                <select
                                                    onChange={onSelectionChange}
                                                    name="basketId" 
                                                    id="basketId"
                                                    className="py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                                    disabled={isLoadingBaskets}>
                                                    <option value="">-select-</option>
                                                    {baskets && (
                                                        baskets.map((basket, index) => <option key={index} value={basket.basketId}>{basket.name}</option>)
                                                    )}
                                                </select>
                                                {isLoadingBaskets && (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 flex-shrink-0"></div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='flex flex-col lg:flex-row lg:items-center gap-2'>
                                            <label className='text-left font-medium whitespace-nowrap min-w-fit'>Rebalancing Date</label>
                                            <div className="flex items-center gap-2 w-full">
                                                <select
                                                    onChange={onDateSelectionChange}
                                                    value={selectedDate}
                                                    name="rebalanceDate" 
                                                    id="rebalanceDate"
                                                    className="py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                                    disabled={uniqueDates.length === 0}>
                                                    <option value="all">All</option>
                                                    {uniqueDates.map((date, index) => (
                                                        <option key={index} value={date}>{date}</option>
                                                    ))}
                                                </select>
                                                {isLoadingData && (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 flex-shrink-0"></div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='flex flex-col lg:flex-row lg:items-center gap-2'>
                                            <label className='text-left font-medium whitespace-nowrap min-w-fit'>Scrip Name</label>
                                            <select
                                                onChange={onScripSelectionChange}
                                                value={selectedScrip}
                                                name="scripName" 
                                                id="scripName"
                                                className="py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                                disabled={selectedDate !== "all" || uniqueScripNames.length === 0}>
                                                <option value="all">All</option>
                                                {uniqueScripNames.map((scripName, index) => (
                                                    <option key={index} value={scripName}>{scripName}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-hidden">
                                <div className="relative max-h-96 overflow-y-auto">
                                    {isLoadingData && (
                                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                                            <div className="flex items-center space-x-2">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                                <span className="text-gray-600">Loading data...</span>
                                            </div>
                                        </div>
                                    )}
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0 z-5">
                                            <tr>
                                                <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 bg-gray-200 uppercase">Scrip Name</th>
                                                <th scope="col" className="px-6 py-2 text-right text-xs font-medium text-gray-500 bg-gray-200 uppercase">Allocation %</th>
                                                <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 bg-gray-200 uppercase">Delta</th>
                                                <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 bg-gray-200 uppercase">Rate</th>
                                                <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 bg-gray-200 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {list && list.length > 0 ? (
                                                list.map((item, index) => {
                                                    const deltaIndicator = getDeltaIndicator(item.allocationDelta, item.currentAllocation);
                                                    return (
                                                        <tr key={index} className={`${index % 2 === 0 ? '' : 'bg-gray-100'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-200`}>
                                                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-800 uppercase">{item.scripName}</td>
                                                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-800 text-right">
                                                                <span className={parseFloat(item.currentAllocation) === 0 ? 'text-orange-600 font-semibold' : ''}>
                                                                    {item.currentAllocation}
                                                                    {parseFloat(item.currentAllocation) === 0 && <span className="ml-1 text-xs">(Exited)</span>}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-800">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className={`${deltaIndicator.color} w-12 text-right`}>{item.allocationDelta}</span>
                                                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${deltaIndicator.bgColor} ${deltaIndicator.color}`}>
                                                                        {deltaIndicator.icon}
                                                                    </span>
                                                                    <span className={`text-xs ${deltaIndicator.color}`} title={deltaIndicator.text}>
                                                                        ({deltaIndicator.text})
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-800">{item.publishedRate}</td>
                                                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-800">{format(item.publishedDate, 'dd/MMM/yyyy')}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                !isLoadingData && (
                                                    <tr>
                                                        <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                                                            {allData.length === 0 
                                                                ? 'Please select a basket to view allocation history' 
                                                                : selectedScrip !== "all" 
                                                                    ? `No data available for scrip "${selectedScrip}" on the selected date`
                                                                    : selectedDate !== "all"
                                                                        ? `No data available for the selected date "${selectedDate}"`
                                                                        : 'No data available'
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );

}

export default AllocationHistoryReport;